/**
 * The one sentence at the top of the analysis PDF: how many metrics cleared, and which did not.
 *
 * 🚨🚨 IT USED TO CONTRADICT ITSELF, and the specimen render caught it:
 *   *"Five of five applicable metrics clear the stated criteria. Going-In Cap and Avg Cash-on-Cash
 *   and IRR and Equity Multiple are the misses."*
 * Five of five clear, and four are misses. The count came from the CALLER (`passed`/`total`, which
 * win over the rows) while the miss list was always computed from `rows` - **two sources for one
 * fact**, which is the defect this codebase keeps paying for. On a document carrying his DRE
 * number, a sentence that argues with itself is worse than a plain one.
 *
 * ⭐⭐ THE RULE HERE: never print a count and a miss-list that disagree. When the rows corroborate
 * the counts, name the misses. When they do not, state the count alone - a narrower true sentence
 * beats a fuller false one.
 */

export interface FindingRow {
  label: string;
  applicable: boolean;
  pass: boolean;
}

const WORDS = ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];
const word = (n: number): string => WORDS[n] ?? String(n);
const Word = (n: number): string => word(n).replace(/^./, (c) => c.toUpperCase());

/**
 * "A", "A and B", "A, B and C".
 *
 * ⭐ The old line joined with ' and ' at every seam - "Going-In Cap and Avg Cash-on-Cash and IRR
 * and Equity Multiple". Serial commas with a single conjunction is how his own analyses read.
 */
export const joinList = (items: string[]): string => {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
};

export interface ScoreFindingInput {
  /** The scorecard rows, when the caller supplied them. */
  rows?: FindingRow[];
  /** The counts the document prints elsewhere - the verdict box and the header both use them. */
  passedCount: number;
  totalCount: number;
  /** Rendered actual for a single miss, e.g. "5.75%". Omitted when there is more than one. */
  describeMiss?: (row: FindingRow) => string | null;
}

/**
 * The finding, guaranteed internally consistent.
 *
 * ⛔ `rowsCorroborate` is the whole guard: the applicable rows must both COUNT the same as the
 * caller's totals and AGREE on how many passed. Anything else and the miss list is not describing
 * the same scorecard as the numbers beside it.
 */
export const scoreFinding = (input: ScoreFindingInput): string => {
  const { passedCount, totalCount, describeMiss } = input;
  const applicable = (input.rows ?? []).filter((r) => r.applicable);
  const misses = applicable.filter((r) => !r.pass);

  const rowsCorroborate =
    applicable.length === totalCount && applicable.length - misses.length === passedCount;

  if (totalCount > 0 && passedCount === totalCount) {
    return `All ${word(totalCount)} applicable metrics clear the stated criteria.`;
  }

  const head = `${Word(passedCount)} of ${word(totalCount)} applicable metrics clear the stated criteria.`;
  if (!rowsCorroborate || misses.length === 0) return head;

  if (misses.length === 1) {
    const actual = describeMiss?.(misses[0]);
    return `${head} The ${misses[0].label}${actual ? ` of ${actual}` : ''} is the miss.`;
  }
  return `${head} ${joinList(misses.map((r) => r.label))} are the misses.`;
};
